/**
 * P-14 — notification inbox. List with read receipts + mark-all-read. Each row shows the title and
 * body the API resolved for it (ACCT-11): a local table covered 12 of the 23 events, so the rest
 * read as raw codes ("order.dispatched").
 */

import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { listNotifications, markAllRead, markRead, type Notification } from "../../api/notifications";

const PAGE_SIZE = 25;


export function Notifications() {
  const qc = useQueryClient();
  // A page at a time, with "Load more" (ACCT-18): it showed the first 50 and no more.
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["notifications"],
    queryFn: ({ pageParam }) => listNotifications(pageParam, PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
  });
  const items = data?.pages.flatMap((p) => p.data) ?? [];
  const unread = data?.pages[0]?.unread ?? 0;
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["notifications"] });
    qc.invalidateQueries({ queryKey: ["notif-unread"] });
  };
  const readOne = useMutation({ mutationFn: (id: string) => markRead(id), onSuccess: invalidate });
  const readAll = useMutation({ mutationFn: () => markAllRead(), onSuccess: invalidate });
  // One mark-read per tap: the row and its link both sent one, and the second got a 404 (ACCT-18).
  const opened = (n: Notification) => {
    if (!n.read_at) readOne.mutate(n.id);
  };

  return (
    <div className="max-w-2xl space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Notifications</h2>
          <p className="text-sm text-gray-500">{data ? `${unread} unread` : " "}</p>
        </div>
        {unread > 0 && (
          <button onClick={() => readAll.mutate()} className="rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50">
            Mark all read
          </button>
        )}
      </div>

      {isLoading && <p className="text-sm text-gray-400">Loading…</p>}
      {data && items.length === 0 && <p className="text-sm text-gray-400">No notifications yet.</p>}

      <ul className="space-y-2">
        {items.map((n) => {
          const body = (
            <div className="flex items-start justify-between gap-3 rounded-xl border bg-white p-4 shadow-xs">
              <div className="min-w-0">
                <div className={`text-sm ${n.read_at ? "text-gray-600" : "font-medium"}`}>{n.title || n.event_code}</div>
                {n.body && <div className="mt-0.5 text-sm text-gray-500">{n.body}</div>}
                <div className="mt-0.5 text-xs text-gray-400">{new Date(n.created_at).toLocaleString()}</div>
              </div>
              {!n.read_at && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand" />}
            </div>
          );
          return (
            <li key={n.id}>
              {n.deep_link ? (
                <Link to={n.deep_link} onClick={() => opened(n)}>
                  {body}
                </Link>
              ) : (
                <div className="cursor-pointer" onClick={() => opened(n)}>
                  {body}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {hasNextPage && (
        <button
          onClick={() => fetchNextPage()}
          disabled={isFetchingNextPage}
          className="w-full rounded-lg border bg-white py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
        >
          {isFetchingNextPage ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
  );
}
